import { useState, useEffect, useCallback } from 'react';
import supabase, { orgId } from '../supabaseClient';

const MODEL_PLATFORMS = [
  { key: 'steam',    label: 'Steam' },
  { key: 'psn',      label: 'PlayStation' },
  { key: 'xbox',     label: 'Xbox' },
  { key: 'nintendo', label: 'Nintendo' },
  { key: 'epic',     label: 'Epic' },
  { key: 'other',    label: 'Other' },
];

export { MODEL_PLATFORMS };

export default function useRevenueUpload() {
  const [uploads, setUploads]           = useState([]);
  const [actuals, setActuals]           = useState({}); // { 'YYYY-MM|platform|game': revenue_gbp }
  const [platformMappings, setPlatformMappings] = useState({}); // { sourcePlatform: modelPlatformKey }
  const [titleMappings, setTitleMappings]       = useState({}); // { sourceTitle: modelGame }
  const [sourcePlatforms, setSourcePlatforms]   = useState([]);
  const [sourceTitles, setSourceTitles]         = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [{ data: ups }, { data: acts }, { data: pm }, { data: tm }] = await Promise.all([
      supabase.from('revenue_uploads').select('*').eq('org_id', orgId).order('uploaded_at', { ascending: false }).limit(20),
      supabase.from('revenue_actuals').select('month_key,source_platform,source_title,revenue_gbp').eq('org_id', orgId),
      supabase.from('revenue_platform_mappings').select('*').eq('org_id', orgId),
      supabase.from('revenue_title_mappings').select('*').eq('org_id', orgId),
    ]);

    setUploads(ups || []);

    // Build actuals lookup
    const actObj = {};
    (acts || []).forEach(r => {
      const key = `${r.month_key}|${r.source_platform}|${r.source_title}`;
      actObj[key] = (actObj[key] || 0) + (r.revenue_gbp || 0);
    });
    setActuals(actObj);

    const pmObj = {};
    (pm || []).forEach(m => { pmObj[m.source_platform] = m.model_platform_key; });
    setPlatformMappings(pmObj);

    const tmObj = {};
    (tm || []).forEach(m => { tmObj[m.source_title] = m.model_game; });
    setTitleMappings(tmObj);

    // Discover platforms and titles from both actuals AND mapping tables
    const platformsFromActs = (acts || []).map(r => r.source_platform).filter(Boolean);
    const titlesFromActs    = (acts || []).map(r => r.source_title).filter(Boolean);
    const platformsFromMaps = (pm || []).map(r => r.source_platform).filter(Boolean);
    const titlesFromMaps    = (tm || []).map(r => r.source_title).filter(Boolean);
    const platforms = [...new Set([...platformsFromActs, ...platformsFromMaps])].sort();
    const titles    = [...new Set([...titlesFromActs, ...titlesFromMaps])].sort();
    setSourcePlatforms(platforms);
    setSourceTitles(titles);

    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Parse Excel file using SheetJS — handles any single-sheet file
  const parseRevenueExcel = useCallback((arrayBuffer) => {
    const XLSX = window.XLSX;
    if (!XLSX) throw new Error('SheetJS not loaded');

    const wb = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
    // Use first sheet or 'All Data' if it exists
    const sheetName = wb.SheetNames.includes('All Data') ? 'All Data' : wb.SheetNames[0];
    const ws = wb.Sheets[sheetName];
    if (!ws) throw new Error('Could not find any sheet in file');

    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });

    // Find header row — look for row with 'MONTH' in first cell
    let headerRow = -1;
    for (let i = 0; i < Math.min(5, rows.length); i++) {
      const cell = String(rows[i]?.[0] || '').toUpperCase();
      if (cell.includes('MONTH')) { headerRow = i; break; }
    }
    if (headerRow === -1) headerRow = 0; // default to first row

    // Detect column positions from header row
    const headerCells = (rows[headerRow] || []).map(c => String(c || '').toUpperCase().trim());
    let colMonth    = 0;
    let colPlatform = -1;
    let colTitle    = -1;
    let colRevenue  = -1;

    headerCells.forEach((h, idx) => {
      if (h.includes('PLATFORM') && colPlatform === -1) colPlatform = idx;
      if ((h.includes('TITLE') || h.includes('GAME') || h === 'GAME') && colTitle === -1) colTitle = idx;
      if ((h.includes('ROYALTY') || h.includes('REVENUE') || h.includes('AMOUNT') || h.includes('GBP')) && colRevenue === -1) colRevenue = idx;
    });

    // Fallback: standard format A=Month, B=Title/Game, C=Platform, D=Revenue
    if (colPlatform === -1) colPlatform = 2;
    if (colTitle    === -1) colTitle    = 1;
    if (colRevenue  === -1) colRevenue  = 3;

    const parsed = [];
    for (let i = headerRow + 1; i < rows.length; i++) {
      const row = rows[i];
      if (!row || !row[colMonth]) continue;

      const rawMonth  = row[colMonth];
      const platform  = String(row[colPlatform] || '').trim();
      const title     = String(row[colTitle]    || '').trim();
      const revRaw    = row[colRevenue];

      if (!platform || !title) continue;

      // Parse month key
      let monthKey = '';
      if (rawMonth instanceof Date) {
        monthKey = `${rawMonth.getFullYear()}-${String(rawMonth.getMonth() + 1).padStart(2, '0')}`;
      } else if (typeof rawMonth === 'number') {
        const d = new Date(Math.round((rawMonth - 25569) * 86400 * 1000));
        monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      } else {
        const m = String(rawMonth).match(/(\d{4})[/-](\d{2})/);
        if (m) monthKey = `${m[1]}-${m[2]}`;
      }
      if (!monthKey) continue;

      // Parse revenue — handle formula errors and nulls
      let revenue = 0;
      if (revRaw !== null && revRaw !== undefined) {
        const v = parseFloat(revRaw);
        if (!isNaN(v)) revenue = v;
      }

      parsed.push({ month_key: monthKey, source_platform: platform, source_title: title, revenue_gbp: revenue });
    }

    return parsed;
  }, []);

  const uploadRevenue = useCallback(async (arrayBuffer, filename) => {
    // Load SheetJS if needed
    if (!window.XLSX) {
      await new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        s.onload = res; s.onerror = rej;
        document.head.appendChild(s);
      });
    }

    const rows = parseRevenueExcel(arrayBuffer);
    if (!rows.length) throw new Error('No data rows found — check the file format');

    const months = [...new Set(rows.map(r => r.month_key))].sort();

    // Delete ALL existing data first before inserting new
    await supabase.from('revenue_actuals').delete().eq('org_id', orgId);
    await supabase.from('revenue_uploads').delete().eq('org_id', orgId);

    // Insert upload record
    const { data: uploadRow, error: upErr } = await supabase.from('revenue_uploads').insert({
      org_id: orgId, filename, months_covered: months, row_count: rows.length,
    }).select('id').single();
    if (upErr) throw upErr;
    const uploadId = uploadRow.id;
    const actRows = rows.map(r => ({ ...r, org_id: orgId, upload_id: uploadId }));
    for (let i = 0; i < actRows.length; i += 200) {
      const { error } = await supabase.from('revenue_actuals').insert(actRows.slice(i, i + 200));
      if (error) console.error('[revenue upload batch]', i, error);
    }

    // Auto-register any new platforms and titles — insert only if not already mapped
    const newPlatforms = [...new Set(rows.map(r => r.source_platform).filter(Boolean))];
    const newTitles    = [...new Set(rows.map(r => r.source_title).filter(Boolean))];

    // Get existing mappings to avoid overwriting them
    const { data: existingPM } = await supabase.from('revenue_platform_mappings').select('source_platform').eq('org_id', orgId);
    const { data: existingTM } = await supabase.from('revenue_title_mappings').select('source_title').eq('org_id', orgId);
    const existingPlatforms = new Set((existingPM || []).map(r => r.source_platform));
    const existingTitles    = new Set((existingTM || []).map(r => r.source_title));

    const newPMRows = newPlatforms.filter(p => !existingPlatforms.has(p))
      .map(p => ({ org_id: orgId, source_platform: p, model_platform_key: null, updated_at: new Date().toISOString() }));
    const newTMRows = newTitles.filter(t => !existingTitles.has(t))
      .map(t => ({ org_id: orgId, source_title: t, model_game: null, updated_at: new Date().toISOString() }));

    if (newPMRows.length > 0) await supabase.from('revenue_platform_mappings').insert(newPMRows);
    if (newTMRows.length > 0) await supabase.from('revenue_title_mappings').insert(newTMRows);

    await load();
    return { rowCount: rows.length, months };
  }, [parseRevenueExcel, load]);

  const savePlatformMapping = useCallback(async (sourcePlatform, modelPlatformKey) => {
    await supabase.from('revenue_platform_mappings').upsert(
      { org_id: orgId, source_platform: sourcePlatform, model_platform_key: modelPlatformKey, updated_at: new Date().toISOString() },
      { onConflict: 'org_id,source_platform' }
    );
    setPlatformMappings(prev => ({ ...prev, [sourcePlatform]: modelPlatformKey }));
  }, []);

  const saveTitleMapping = useCallback(async (sourceTitle, modelGame) => {
    await supabase.from('revenue_title_mappings').upsert(
      { org_id: orgId, source_title: sourceTitle, model_game: modelGame, updated_at: new Date().toISOString() },
      { onConflict: 'org_id,source_title' }
    );
    setTitleMappings(prev => ({ ...prev, [sourceTitle]: modelGame }));
  }, []);

  // Get revenue for a specific model game + platform + month
  const getRevenueActual = useCallback((modelGame, modelPlatformKey, monthKey) => {
    let total = 0;
    let found = false;
    // Find all source combos that map to this game+platform
    sourceTitles.forEach(title => {
      if (titleMappings[title] !== modelGame) return;
      sourcePlatforms.forEach(platform => {
        if (platformMappings[platform] !== modelPlatformKey) return;
        found = true;
        const key = `${monthKey}|${platform}|${title}`;
        total += actuals[key] || 0;
      });
    });
    // Return null if no mapping exists (so forecast is shown), 0 if mapped but no data
    return found ? total : null;
  }, [actuals, titleMappings, platformMappings, sourceTitles, sourcePlatforms]);

  return {
    uploads, actuals, platformMappings, titleMappings,
    sourcePlatforms, sourceTitles, loading,
    uploadRevenue, savePlatformMapping, saveTitleMapping,
    getRevenueActual,
  };
}
